import { cn } from '@/utils/cn';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  variant?: 'default' | 'elevated' | 'muted';
  onClick?: () => void;
}

const paddingStyles = {
  none: '',
  sm: 'p-3 sm:p-4',
  md: 'p-4 sm:p-5',
  lg: 'p-5 sm:p-6',
};

const variantStyles = {
  default:
    'border-gray-200/70 bg-white shadow-soft dark:border-gray-700/50 dark:bg-gray-800/90',
  elevated:
    'border-gray-200/60 bg-white shadow-card dark:border-gray-700/40 dark:bg-gray-800/95',
  muted:
    'border-gray-100 bg-gray-50/80 shadow-none dark:border-gray-800 dark:bg-gray-900/60',
};

function Card({
  children,
  className,
  padding = 'md',
  hover = false,
  variant = 'default',
  onClick,
}: CardProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-xl border transition-all duration-200',
        variantStyles[variant],
        hover &&
          'cursor-pointer hover:border-gray-300 hover:shadow-elevated dark:hover:border-gray-600',
        paddingStyles[padding],
        className,
      )}
    >
      {children}
    </div>
  );
}

function CardHeader({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      {children}
    </div>
  );
}

function CardTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <h3 className={cn('text-sm font-semibold text-gray-900 dark:text-gray-100', className)}>
      {children}
    </h3>
  );
}

function CardContent({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('mt-3', className)}>{children}</div>;
}

export { Card, CardHeader, CardTitle, CardContent };
